import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ArrowsLeftRight, MagnifyingGlass, Plus, Sparkle, DotsSixVertical, LinkSimple, X } from "@phosphor-icons/react";
import ConvertirMovimientoDialog from "@/components/ConvertirMovimientoDialog";
import CrearDesdeExtractoDialog from "@/components/CrearDesdeExtractoDialog";

const ENTRA_OP = (t) => t === "cobro" || t === "traspaso_in";
const esImportado = (m) => !!m.import_batch_id || m.referencia_tipo === "excel";
const TIPO_OP_LABEL = { cobro: "Cobro", pago: "Pago", gasto: "Gasto", traspaso_in: "Traspaso recibido", traspaso_out: "Traspaso enviado", pago_pasivo: "Pago pasivo" };
const keyOf = (entra, monto) => `${entra ? "in" : "out"}|${Math.round(Number(monto || 0) * 100)}`;
const sum = (arr, f) => arr.reduce((s, x) => s + Number(f(x) || 0), 0);

export default function ConciliacionTab({ cajas, cuentas, onChanged }) {
  const [ops, setOps] = useState([]);
  const [ext, setExt] = useState([]);
  const [loading, setLoading] = useState(false);
  const [caja, setCaja] = useState("__all__");
  const [q, setQ] = useState("");
  const [selOps, setSelOps] = useState(new Set());
  const [selExt, setSelExt] = useState(new Set());
  const [drag, setDrag] = useState(null); // { lado: 'ops'|'ext', id }
  const [over, setOver] = useState(null); // id de la fila destino
  const [convertir, setConvertir] = useState(null);
  const [crear, setCrear] = useState(null); // { mov, modo }
  const [uniendo, setUniendo] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/conciliacion/pendientes-sistema"), api.get("/movimientos-caja/pendientes")]);
      setOps(a.data.operaciones || []);
      setExt(b.data || []);
      setSelOps(new Set()); setSelExt(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la conciliación");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtro = (texto, cajaId) => (caja === "__all__" || cajaId === caja) && (!q || (texto || "").toLowerCase().includes(q.toLowerCase()));
  const opsV = useMemo(() => ops.filter((o) => filtro(`${o.contraparte_nombre} ${o.concepto} ${(o.comprobantes || []).join(" ")}`, o.caja_id)), [ops, caja, q]);
  const extV = useMemo(() => ext.filter((m) => filtro(m.concepto, m.caja_id)), [ext, caja, q]);

  // Sugerencias: mismo sentido + mismo monto exacto
  const keysOps = useMemo(() => { const m = {}; ops.forEach((o) => { const k = keyOf(ENTRA_OP(o.tipo), o.monto_caja); (m[k] ||= []).push(o.id); }); return m; }, [ops]);
  const keysExt = useMemo(() => { const m = {}; ext.forEach((e) => { if (!esImportado(e)) return; const k = keyOf(e.tipo === "ingreso", e.monto); (m[k] ||= []).push(e.id); }); return m; }, [ext]);

  const toggle = (setter) => (id) => setter((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const opsSel = ops.filter((o) => selOps.has(o.id));
  const extSel = ext.filter((e) => selExt.has(e.id));
  const totOps = sum(opsSel, (o) => o.monto_caja);
  const totExt = sum(extSel, (e) => e.monto);
  const dif = Math.round((totExt - totOps) * 100) / 100;

  const unir = async (movIds, opIds) => {
    if (!movIds.length || !opIds.length) return toast.error("Elegí al menos una fila de cada lado");
    const mExt = ext.filter((e) => movIds.includes(e.id));
    const mOps = ops.filter((o) => opIds.includes(o.id));
    const d = Math.round((sum(mExt, (e) => e.monto) - sum(mOps, (o) => o.monto_caja)) * 100) / 100;
    if (Math.abs(d) >= 0.01 && !window.confirm(`Los montos difieren en ${fmtMoney(Math.abs(d))}. La diferencia quedará como movimiento a clasificar (comisión, redondeo). ¿Unir igual?`)) return;
    setUniendo(true);
    try {
      const { data } = await api.post("/conciliacion/asociar-multi", { mov_ids: movIds, cobro_pago_ids: opIds });
      toast.success(`Conciliado${data.cajas_corregidas?.length ? ` · se corrigió la caja desde ${data.cajas_corregidas.join(", ")}` : ""}${Math.abs(data.diferencia || 0) >= 0.01 ? ` · diferencia ${fmtMoney(Math.abs(data.diferencia))} a clasificar` : ""}`);
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo conciliar");
    } finally { setUniendo(false); }
  };

  // Drag & drop nativo: arrastrar una fila sobre una del otro lado une ambas (más lo seleccionado de cada lado)
  const onDragStart = (lado, id) => (e) => { setDrag({ lado, id }); e.dataTransfer.effectAllowed = "link"; };
  const onDrop = (ladoDestino, id) => (e) => {
    e.preventDefault(); setOver(null);
    if (!drag || drag.lado === ladoDestino) return setDrag(null);
    const extIds = new Set(selExt), opIds = new Set(selOps);
    (drag.lado === "ext" ? extIds : opIds).add(drag.id);
    (ladoDestino === "ext" ? extIds : opIds).add(id);
    setDrag(null);
    unir([...extIds], [...opIds]);
  };
  const dropProps = (lado, id) => ({
    onDragOver: (e) => { if (drag && drag.lado !== lado) { e.preventDefault(); e.dataTransfer.dropEffect = "link"; setOver(id); } },
    onDragLeave: () => setOver((o) => (o === id ? null : o)),
    onDrop: onDrop(lado, id),
  });

  const cajaNombre = (id) => cajas.find((c) => c.id === id)?.nombre || "";
  const dupCount = extV.filter((m) => m.duplicado).length;

  const eliminarExtracto = async (m) => {
    const msg = m.duplicado
      ? "¿Eliminar esta línea duplicada del extracto?" + (m.afecta_saldo !== false ? " Se descuenta del saldo de la caja lo que había sumado de más." : "")
      : "¿Eliminar esta línea del extracto? No se crea nada en el sistema." + (m.afecta_saldo !== false ? " Se revierte su efecto en el saldo." : "");
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/movimientos-caja/${m.id}`);
      toast.success("Línea eliminada");
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };
  const eliminarDuplicados = async () => {
    if (!window.confirm(`¿Eliminar ${dupCount} línea(s) duplicada(s) del extracto? Las que estaban sumadas al saldo se descuentan.`)) return;
    try {
      const { data } = await api.post("/movimientos-caja/eliminar-duplicados", null, { params: caja !== "__all__" ? { caja_id: caja } : {} });
      toast.success(`${data.eliminados} duplicado(s) eliminados`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudieron eliminar"); }
  };

  return (
    <div className="space-y-3" data-testid="conciliacion-tab">
      {/* Filtros + barra de unión */}
      <div className="border border-[#E5E5E5] bg-white rounded-sm px-4 py-2.5 flex flex-wrap items-center gap-3">
        <Select value={caja} onValueChange={setCaja}>
          <SelectTrigger className="h-8 w-56 rounded-sm text-[12px]" data-testid="conc-filtro-caja"><SelectValue placeholder="Caja" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas las cajas</SelectItem>
            {cajas.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <MagnifyingGlass size={14} className="text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar concepto, cliente, proveedor…" className="h-8 w-72 rounded-sm text-[12px]" data-testid="conc-buscar" />
        </div>
        <div className="ml-auto flex items-center gap-3 text-[12px] font-mono">
          {(selOps.size > 0 || selExt.size > 0) && (
            <>
              <span className="text-neutral-600">Sistema <b data-testid="conc-sel-ops">{selOps.size}</b> · {fmtMoney(totOps)}</span>
              <ArrowsLeftRight size={14} className="text-neutral-400" />
              <span className="text-neutral-600">Extracto <b data-testid="conc-sel-ext">{selExt.size}</b> · {fmtMoney(totExt)}</span>
              {selOps.size > 0 && selExt.size > 0 && (
                <span className={Math.abs(dif) < 0.01 ? "text-emerald-700" : "text-amber-700"} data-testid="conc-sel-dif">
                  {Math.abs(dif) < 0.01 ? "✓ montos iguales" : `dif. ${fmtMoney(Math.abs(dif))}`}
                </span>
              )}
              <Button size="sm" className="h-7 rounded-sm bg-[#6B1F24] hover:bg-[#571519] gap-1" disabled={uniendo || !selOps.size || !selExt.size}
                      onClick={() => unir([...selExt], [...selOps])} data-testid="conc-unir-btn">
                <LinkSimple size={13} weight="bold" /> Unir seleccionados
              </Button>
              <button type="button" onClick={() => { setSelOps(new Set()); setSelExt(new Set()); }} className="text-neutral-400 hover:text-neutral-700" title="Limpiar selección" data-testid="conc-limpiar-sel"><X size={14} /></button>
            </>
          )}
          {!selOps.size && !selExt.size && <span className="text-neutral-400">Arrastrá una fila sobre otra del lado opuesto para unirlas, o tildá varias y “Unir seleccionados”.</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* SISTEMA (izquierda) */}
        <Columna titulo="Sistema sin conciliar" sub="cobros, pagos, gastos, traspasos cargados en el sistema" count={opsV.length} testid="conc-col-sistema" loading={loading}>
          {opsV.map((o) => {
            const entra = ENTRA_OP(o.tipo);
            const sug = (keysExt[keyOf(entra, o.monto_caja)] || []).length;
            return (
              <Fila key={o.id} id={o.id} lado="ops" sel={selOps.has(o.id)} onToggle={toggle(setSelOps)} onDragStart={onDragStart("ops", o.id)}
                    over={over === o.id} dragging={drag?.id === o.id} dropProps={dropProps("ops", o.id)} testid={`conc-op-${o.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-[11px] font-mono text-neutral-500">
                    <span>{fmtDate(o.fecha)}</span><span>·</span><span className="truncate">{o.caja_nombre || cajaNombre(o.caja_id)}</span>
                    <span className="px-1.5 rounded-sm bg-neutral-100 text-neutral-600">{TIPO_OP_LABEL[o.tipo] || o.tipo}</span>
                    {sug > 0 && <span className="inline-flex items-center gap-0.5 text-emerald-700" title="Hay un movimiento del extracto con el mismo monto"><Sparkle size={11} weight="fill" /> {sug}</span>}
                  </div>
                  <div className="text-[13px] font-medium truncate" title={o.contraparte_nombre}>{o.contraparte_nombre}</div>
                  {(o.comprobantes?.length > 0 || o.concepto) && <div className="text-[11px] text-neutral-500 truncate">{o.comprobantes?.length ? o.comprobantes.join(", ") : o.concepto}</div>}
                </div>
                <div className={`font-mono text-[13px] font-semibold whitespace-nowrap ${entra ? "num-pos" : "num-neg"}`}>{entra ? "+" : "-"} {fmtMoney(o.monto_caja, o.moneda)}</div>
              </Fila>
            );
          })}
          {!loading && opsV.length === 0 && <Vacio>No hay operaciones del sistema sin conciliar.</Vacio>}
        </Columna>

        {/* EXTRACTO (derecha) */}
        <Columna titulo="Extracto bancario" sub="movimientos importados que todavía no están en el sistema" count={extV.length} testid="conc-col-extracto" loading={loading}
                 accion={dupCount > 0 && (
                   <Button size="sm" variant="outline" onClick={eliminarDuplicados} className="h-7 rounded-sm text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="conc-eliminar-duplicados">
                     Eliminar {dupCount} duplicado{dupCount > 1 ? "s" : ""}
                   </Button>
                 )}>
          {extV.map((m) => {
            const entra = m.tipo === "ingreso";
            const importado = esImportado(m);
            const candidatos = importado ? (keysOps[keyOf(entra, m.monto)] || []) : [];
            return (
              <Fila key={m.id} id={m.id} lado="ext" sel={selExt.has(m.id)} onToggle={importado ? toggle(setSelExt) : undefined} onDragStart={importado ? onDragStart("ext", m.id) : undefined}
                    over={over === m.id} dragging={drag?.id === m.id} dropProps={importado ? dropProps("ext", m.id) : {}} testid={`conc-ext-${m.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-[11px] font-mono text-neutral-500">
                    <span>{fmtDate(m.fecha)}</span><span>·</span><span className="truncate">{m.caja_nombre || cajaNombre(m.caja_id)}</span>
                    {m.numero_movimiento && <span>#{m.numero_movimiento}</span>}
                    {!importado && <span className="px-1.5 rounded-sm bg-amber-50 text-amber-700">diferencia de conciliación</span>}
                    {m.duplicado && (
                      <span className="px-1.5 rounded-sm bg-rose-100 text-rose-700 font-semibold" data-testid={`conc-dup-${m.id}`}
                            title={m.duplicado === "conciliado" ? "Esta línea del extracto se importó dos veces y su otra copia ya fue conciliada: sobra" : "Hay otra copia idéntica pendiente"}>
                        duplicado{m.duplicado === "conciliado" ? " · ya conciliado" : ""}
                      </span>
                    )}
                    {m.afecta_saldo !== false && importado && <span className="px-1.5 rounded-sm bg-neutral-100 text-neutral-500" title="Importado con el modelo anterior: ya está sumado al saldo">en saldo</span>}
                    {candidatos.length === 1 && (
                      <button type="button" onClick={() => unir([m.id], candidatos)} className="inline-flex items-center gap-0.5 text-emerald-700 hover:underline" data-testid={`conc-sug-${m.id}`}>
                        <Sparkle size={11} weight="fill" /> unir con sugerido
                      </button>
                    )}
                    {candidatos.length > 1 && <span className="inline-flex items-center gap-0.5 text-emerald-700"><Sparkle size={11} weight="fill" /> {candidatos.length} posibles</span>}
                  </div>
                  <div className="text-[13px] truncate" title={m.concepto}>{m.concepto}</div>
                  {m.cuenta_sugerida && <div className="text-[11px] text-sky-700 truncate">Regla: {m.cuenta_sugerida.cuenta_codigo} · {m.cuenta_sugerida.cuenta_nombre}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <div className={`font-mono text-[13px] font-semibold whitespace-nowrap ${entra ? "num-pos" : "num-neg"}`}>{entra ? "+" : "-"} {fmtMoney(m.monto, m.moneda)}</div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline" className="h-7 px-2 rounded-sm text-[11px] gap-1 border-[#6B1F24] text-[#6B1F24] hover:bg-[#F5F1E9]" data-testid={`conc-crear-${m.id}`}>
                        <Plus size={12} weight="bold" /> Crear
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="text-[12px]">
                      {importado && <DropdownMenuItem onClick={() => setConvertir(m)} data-testid={`conc-crear-cp-${m.id}`}>{entra ? "Cobro de cliente" : "Pago a proveedor"}</DropdownMenuItem>}
                      {importado && !entra && <DropdownMenuItem onClick={() => setCrear({ mov: m, modo: "gasto" })} data-testid={`conc-crear-gasto-${m.id}`}>Gasto de estructura</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => setCrear({ mov: m, modo: "movimiento" })} data-testid={`conc-crear-mov-${m.id}`}>Movimiento con cuenta contable</DropdownMenuItem>
                      {importado && <DropdownMenuItem onClick={() => setCrear({ mov: m, modo: "traspaso" })} data-testid={`conc-crear-traspaso-${m.id}`}>Traspaso entre cajas</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => eliminarExtracto(m)} className="text-rose-700 focus:text-rose-700" data-testid={`conc-eliminar-${m.id}`}>
                        {m.duplicado ? "Eliminar duplicado" : "Eliminar línea del extracto"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </Fila>
            );
          })}
          {!loading && extV.length === 0 && <Vacio>No hay movimientos del extracto pendientes. Importá un Excel desde “Importar Excel”.</Vacio>}
        </Columna>
      </div>

      {convertir && <ConvertirMovimientoDialog mov={convertir} onClose={() => setConvertir(null)} onDone={() => { load(); onChanged?.(); }} />}
      {crear && <CrearDesdeExtractoDialog mov={crear.mov} modo={crear.modo} cajas={cajas} cuentas={cuentas} onClose={() => setCrear(null)} onDone={() => { load(); onChanged?.(); }} />}
    </div>
  );
}

function Columna({ titulo, sub, count, children, testid, loading, accion }) {
  return (
    <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden" data-testid={testid}>
      <div className="px-4 py-2.5 border-b bg-neutral-50 flex items-center justify-between gap-2">
        <div>
          <div className="font-heading font-semibold text-[14px]">{titulo}</div>
          <div className="text-[11px] text-neutral-500">{sub}</div>
        </div>
        <div className="flex items-center gap-2">
          {accion}
          <span className="font-mono text-[11px] px-2 py-0.5 rounded-sm bg-[#6B1F24] text-white" data-testid={`${testid}-count`}>{count}</span>
        </div>
      </div>
      <div className="max-h-[70vh] overflow-y-auto divide-y divide-[#F0F0F0]">
        {loading && <div className="px-4 py-6 text-center text-[12px] text-neutral-400">Cargando…</div>}
        {children}
      </div>
    </div>
  );
}

function Vacio({ children }) {
  return <div className="px-4 py-8 text-center text-[12px] text-neutral-400">{children}</div>;
}

function Fila({ id, sel, onToggle, onDragStart, over, dragging, dropProps, children, testid }) {
  const arrastrable = !!onDragStart;
  return (
    <div
      draggable={arrastrable}
      onDragStart={onDragStart}
      onDragEnd={() => {}}
      {...dropProps}
      className={`flex items-center gap-2 px-3 py-2 transition-colors ${arrastrable ? "cursor-grab active:cursor-grabbing" : ""} ${sel ? "bg-[#F5F1E9]" : "hover:bg-neutral-50"} ${over ? "ring-2 ring-inset ring-emerald-500 bg-emerald-50" : ""} ${dragging ? "opacity-40" : ""}`}
      data-testid={testid}
    >
      {arrastrable ? <DotsSixVertical size={14} className="text-neutral-300 shrink-0" /> : <span className="w-[14px] shrink-0" />}
      {onToggle ? (
        <input type="checkbox" checked={sel} onChange={() => onToggle(id)} className="accent-[#6B1F24] shrink-0" data-testid={`${testid}-check`} />
      ) : <span className="w-[13px] shrink-0" />}
      {children}
    </div>
  );
}
